// WHO IS BILLED AT WHAT, AND WHO IS ALLOWED TO SEE IT.
//
// Owner, 2026-10-07: "an hourly rate override options that generates the cost
// based on an overriden hourly rate for certain contractors, and who should
// have the overriden cost affecting their reports and their digests".
//
// THE SECOND HALF IS THE CONTROL THAT MATTERS, and it is why this is a radio
// and not a checkbox buried in a drawer:
//
//   Your account only    the override prices the work in YOUR reports. The
//                        contractor keeps seeing the rate that was recorded
//                        when they submitted. This is the normal case - what
//                        you charge on is not what you told them they bill at.
//   Also show it to      the override also reaches the contractor's own
//   the contractor       emails (their digest and weekly report). It changes
//                        what somebody else reads about their own money, so
//                        it is never the default. Labels and help text: see
//                        rateAudience.jsx (renamed 2026-10-11 from "Visible
//                        to them too", which the owner could not place).
//
// THE OVERRIDE IS THE COST (owner, 2026-10-10: "all cost should reflect the
// override, on digest, export ..."). The ledger still keeps what was agreed at
// submission and the line items show it as "recorded", so a re-priced figure
// can always be told from a recorded one.
//
// ONLY A COMPANY ADMIN OR SUPER ADMIN CHANGES ONE (`canEdit`); anybody else
// who may read the report sees the list without the buttons. The server
// checks again. A rate for a single issue or drawing is set on its line.
import React, { useCallback, useEffect, useState } from "react";
import { Alert, Button, Card, Input, InputNumber, Modal, Popconfirm, Select, Table, Tag, Tooltip, message } from "antd";
import { AUDIENCE_HELP, AUDIENCE_LABEL, WhoSeesRate } from "./rateAudience";
import { usePartnerTrades } from "components/Partners/partnerTrades";
import { PartnerWithTrades } from "components/Partners/PartnerTradeLabel";
import { getOverrides, setOverride, removeOverride } from "api/billableHours";
import { matchOption } from "components/SearchSelect/searchSelect";

const T = { muted: "#667085", amber: "#b54708" };
const SYSTEMS = [
  { value: "issues", label: "Issues" },
  { value: "shopDrawings", label: "Shop drawings" },
];

// Per operation since 2026-10-14 (contractorRatesAdd / Edit / Delete, and
// contractorRatesView to see the list at all). `canEdit` alone still means all
// three, for callers that pass only that.
export default function RateOverrides({
  contractors = [], projects = [], onChanged, canEdit = false,
  canAdd = canEdit, canEditRates = canEdit, canDelete = canEdit, canViewRates = true,
}) {
  if (!canViewRates) return null;
  return (
    <RateOverridesCard contractors={contractors} projects={projects} onChanged={onChanged}
                       canEdit={canAdd || canEditRates || canDelete} canAdd={canAdd} canEditRates={canEditRates} canDelete={canDelete} />
  );
}

function RateOverridesCard({ contractors, projects, onChanged, canEdit, canAdd, canEditRates, canDelete }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(null);
  const overrideTrades = usePartnerTrades((editing && editing.projectId) || null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    getOverrides()
      .then((d) => { setRows(d.overrides || []); setError(""); })
      .catch((e) => setError((e && (e.err || e.message)) || "Could not read the overrides"))
      .finally(() => setLoading(false));
  }, []);
  useEffect(load, [load]);

  const save = () => {
    if (!editing) return;
    setSaving(true);
    setOverride({
      contractorCompanyId: editing.contractorCompanyId,
      contractorName: editing.contractorName,
      rate: editing.rate,
      appliesTo: editing.appliesTo,
      projectId: editing.projectId || undefined,
      systems: editing.systems || [],
      reason: editing.reason || "",
    })
      .then(() => {
        message.success(
          editing.appliesTo === "everyone"
            ? "Saved. Your reports use this rate, and so do the contractor's own emails"
            : "Saved. Your reports use this rate; the contractor keeps their recorded rate"
        );
        setEditing(null);
        load();
        if (onChanged) onChanged();
      })
      // THE SERVER'S WORDS, including the 409 that explains why a second
      // override for one scope is refused rather than silently replacing one.
      .catch((e) => message.error((e && (e.err || e.message)) || "Could not save that"))
      .finally(() => setSaving(false));
  };

  const drop = (row) => {
    removeOverride(row._id)
      .then(() => { message.success("Override removed — the recorded rates apply again"); load(); if (onChanged) onChanged(); })
      .catch((e) => message.error((e && (e.err || e.message)) || "Could not remove it"));
  };

  const columns = [
    { title: "Contractor", key: "c", render: (_, r) => r.contractorName || r.contractorCompanyId },
    { title: "Rate", key: "r", align: "right", render: (_, r) => <b>${Number(r.rate).toLocaleString()}/h</b> },
    {
      title: "Applies to", key: "a",
      render: (_, r) => (r.appliesTo === "everyone"
        ? <Tooltip title={AUDIENCE_HELP.everyone}><Tag color="volcano">{AUDIENCE_LABEL.everyone}</Tag></Tooltip>
        : <Tooltip title={AUDIENCE_HELP.owner}><Tag>{AUDIENCE_LABEL.owner}</Tag></Tooltip>),
    },
    {
      title: "Scope", key: "s",
      render: (_, r) => {
        const p = projects.find((x) => String(x._id) === String(r.projectId));
        return (
          <span>
            <div>{r.projectId ? (p ? p.title : "one project") : "Every project"}</div>
            <div style={{ color: T.muted, fontSize: 12 }}>
              {r.systems && r.systems.length
                ? r.systems.map((x) => (SYSTEMS.find((y) => y.value === x) || {}).label || x).join(", ")
                : "All systems"}
            </div>
          </span>
        );
      },
    },
    { title: "Why", dataIndex: "reason", key: "w", render: (v) => <span style={{ color: T.muted, fontSize: 12 }}>{v || "—"}</span> },
    ...(!canEdit ? [] : [{
      title: "", key: "x", width: 150,
      render: (_, r) => (
        <span>
          {canEditRates ? <Button size="small" type="link" onClick={() => setEditing({ ...r })}>Edit</Button> : null}
          {canDelete ? (
            <Popconfirm
              title="Remove this override?"
              description="The rates recorded at submission apply again. Nothing in the ledger changes."
              onConfirm={() => drop(r)}
            >
              <Button size="small" type="link" danger>Remove</Button>
            </Popconfirm>
          ) : null}
        </span>
      ),
    }]),
  ];

  return (
    <Card
      size="small"
      title="Hourly rate overrides"
      extra={canAdd ? <Button size="small" type="primary" onClick={() => setEditing({ appliesTo: "owner", rate: null, systems: [] })}>Add an override</Button> : null}
      style={{ marginBottom: 12 }}
    >
      <div style={{ color: T.muted, fontSize: 12.5, marginBottom: 8 }}>
        An override prices a contractor&apos;s hours at a rate you choose, on every project or one. That rate becomes
        the cost on this page, in the exports and in the digests; each line still shows the rate recorded when the
        work was submitted. A rate for one issue or shop drawing is set on its line below, and comes first.
        {canEdit ? null : " Only a company administrator, or someone given the contractor rates permissions, can change these."}
      </div>
      {error ? <Alert type="error" showIcon message={error} /> : null}
      <Table
        size="small" rowKey="_id" loading={loading} dataSource={rows} columns={columns}
        pagination={false}
        locale={{ emptyText: "No overrides. Every contractor is costed at the rate recorded when they submitted." }}
      />

      <Modal
        open={!!editing}
        title={editing && editing._id ? "Edit the override" : "Add an override"}
        onCancel={() => setEditing(null)}
        onOk={save}
        okButtonProps={{ loading: saving, disabled: !editing || !editing.contractorCompanyId || editing.rate == null }}
        okText="Save"
        width={560}
      >
        {editing ? (
          <div style={{ display: "grid", gap: 12 }}>
            <div>
              <div style={{ color: T.muted, fontSize: 12.5, marginBottom: 4 }}>Contractor</div>
              <Select filterOption={matchOption}
                style={{ width: "100%" }} showSearch
                placeholder="Which contractor"
                value={editing.contractorCompanyId || undefined}
                onChange={(v) => {
                  const c = contractors.find((x) => String(x.companyId) === String(v));
                  setEditing((s) => ({ ...s, contractorCompanyId: v, contractorName: (c && c.companyName) || "" }));
                }}
                optionFilterProp="search" optionLabelProp="text"
                options={contractors.filter((c) => c.companyId).map((c) => {
                  // Trades beneath, for an override on one project (owner, 2026-10-16).
                  const name = c.companyName || c.companyId;
                  const t = overrideTrades.tradesOf(c.companyId);
                  return { value: c.companyId, text: name, search: `${name} ${t.join(" ")}`, label: t.length ? <PartnerWithTrades name={name} trades={t} /> : name };
                })}
              />
            </div>
            <div>
              <div style={{ color: T.muted, fontSize: 12.5, marginBottom: 4 }}>Hourly rate</div>
              <InputNumber
                style={{ width: 180 }} min={0} max={10000} prefix="$"
                value={editing.rate}
                onChange={(v) => setEditing((s) => ({ ...s, rate: v }))}
              />
              <span style={{ color: T.muted, fontSize: 12, marginLeft: 8 }}>per hour</span>
            </div>
            <div>
              <div style={{ color: T.muted, fontSize: 12.5, marginBottom: 4 }}>Who sees this rate</div>
              <WhoSeesRate value={editing.appliesTo} onChange={(v) => setEditing((st) => ({ ...st, appliesTo: v }))} />
            </div>
            <div>
              <div style={{ color: T.muted, fontSize: 12.5, marginBottom: 4 }}>Scope</div>
              <Select filterOption={matchOption}
                style={{ width: "100%" }} allowClear showSearch optionFilterProp="label"
                placeholder="Every project on the account"
                value={editing.projectId || undefined}
                onChange={(v) => setEditing((s) => ({ ...s, projectId: v || "" }))}
                options={projects.map((p) => ({ value: p._id, label: p.title || p._id }))}
              />
              <Select
                mode="multiple" style={{ width: "100%", marginTop: 8 }} allowClear
                placeholder="All systems"
                value={editing.systems || []}
                onChange={(v) => setEditing((s) => ({ ...s, systems: v }))}
                options={SYSTEMS}
              />
            </div>
            <div>
              <div style={{ color: T.muted, fontSize: 12.5, marginBottom: 4 }}>Why (optional, but it is what you will want in six months)</div>
              <Input.TextArea
                rows={2} maxLength={500}
                value={editing.reason || ""}
                onChange={(e) => setEditing((s) => ({ ...s, reason: e.target.value }))}
              />
            </div>
          </div>
        ) : null}
      </Modal>
    </Card>
  );
}
